import {
  ComponentType,
  createElement,
  lazy as reactLazy,
  useCallback,
  forwardRef,
  useRef,
} from 'react';
import { matchPath } from 'react-router-dom';
import ViewError from '~/components/ViewError';

const routes: { path: string; factoryId: string }[] = [];

const components: {
  factoryId: string;
  preload: () => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ComponentType<any>;
}[] = [];

/**
 * Returns a lazy loaded Component with an additional function to preload it
 */
export const lazyWithPreload = <T extends Record<string, unknown>>(
  path: string,
  factory: () => Promise<{ default: ComponentType<T> }>,
) => {
  const factoryId = factory.toString();

  if (!routes.find((item) => item.path === path)) {
    routes.push({ path, factoryId });
  }

  const componentExists = components.find((item) => item.factoryId === factoryId);
  if (componentExists) {
    return {
      path,
      Component: componentExists.Component,
    };
  }

  let factoryPromise: Promise<void>;
  let LoadedComponent: ComponentType<T> | undefined;
  const LazyLoadedComponent = reactLazy(async () => {
    return factory().catch((error) => {
      // eslint-disable-next-line no-console
      console.error(`Failed to lazy load component path=${path}`, error);
      return { default: ViewError as ComponentType<T> };
    });
  }) as unknown as ComponentType<T>;

  const Component = forwardRef<T>((props, ref) => {
    const Comp = LoadedComponent ?? LazyLoadedComponent;
    return createElement<T>(Comp, Object.assign(ref ? { ref } : {}, props) as T);
  }) as ComponentType<T>;

  const preload = () => {
    if (!factoryPromise) {
      factoryPromise = factory()
        .then((module) => {
          LoadedComponent = module.default;
        })
        .catch(() => {
          // Silent component preload fail
        });
    }

    return factoryPromise;
  };

  components.push({ factoryId, preload, Component });

  return { path, Component };
};

/**
 * Preload a component that matches a specific route
 */
const usePreload = () => {
  const preloaded = useRef<boolean>(false);

  const preload = useCallback((route: string) => {
    if (preloaded.current) {
      return;
    }

    const match = routes.find(({ path }) =>
      matchPath(
        {
          path,
          end: true,
        },
        route,
      ),
    );

    if (match) {
      const component = components.find((item) => item.factoryId === match.factoryId);
      component?.preload();
      preloaded.current = true;
    }
  }, []);

  return preload;
};

export default usePreload;
